import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from 'react';
import styled, { stylesheet } from 'astroturf/react';
import pluralize from 'pluralize';
import { currentUser } from 'js/utils/user';
import { Body as BodyBase } from 'js/design_system/Body';
import {
  DismissalReason,
  FollowableUser,
  isPaper,
  News,
  NewsType,
  Paper,
  ResourceType,
  SlimPost,
  ResourcePayload,
} from 'js/social/shared/types';
import ViewsWatcher from 'js/social/shared/ViewsWatcher';
import {
  ImpressionWatcher,
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import {
  getImpressionPaperType,
  getSocialImpressionSource,
  getImpressionFeedExtraData,
  getImpressionPage,
  makeImpressionPropsGenerator,
} from 'js/social/ImpressionHelper';
import CreateSocialItem from 'js/social/shared/CreateSocialItem/CreateSocialItem';
import useCurrentUserAsAuthor from 'js/social/shared/CreateSocialItem/useCurrentUserAsAuthor';
import { legacyRecordNewsfeedView } from 'js/social/api/legacy_views_reporting';
import { createNewsView } from 'js/social/api/viewed_news_api';
import { FeedCommentsActions } from 'js/social/Comment/useCommentInteractions';
import useConfirmationModal from 'js/social/shared/ConfirmationModal/useConfirmationModal';
import useElementVisibilities from 'js/social/News/hooks/useElementVisibilities';
import { SocialActionsMenu } from 'js/social/shared/Menu/SocialActionsMenu';
import useMenuActions from 'js/social/shared/Menu/useMenuActions';
import {
  BlockIcon,
  DeleteInactive,
  FlagInactive,
  FolderInactive,
  AddPersonInactive,
  HiddenIcon,
  Edit,
} from 'js/social/shared/Icons';
import UnTagOutlineIcon from 'js/svg/colorless/untag_outline.svg';
import AccountRemoveOutlineIcon from 'js/svg/colorless/account_remove_outline.svg';
import PaperContainer from 'js/social/PaperContainer';
import useShowToggle from 'js/social/shared/useShowToggle';
import { fsEvent } from 'js/fullstory';
import Base from './Base';
import { PostBody } from './Body';
import { Reason } from 'js/social/shared/Reason';
import Stats from './Stats';
import PostCommentsBase from './PostComments';
import { Header } from './Header';
import { Cta } from './Cta';
import useNewsInteractions, { FeedPostsActions } from './useNewsInteractions';
import { FeedContext } from '../contexts/feed';
import { PostImage } from 'js/social/shared/PostImage';
import { FollowActionButton } from 'js/social/shared/FollowActionButton';
import {
  PostEngagementFollowPrompt,
  PostEngagementFollowPromptProps,
} from 'js/social/shared/PostEngagementFollowPrompt';
import { RemovedItemNotice } from 'js/social/News/RemovedItemNotice';
import { PostPrompt } from 'js/social/PostPrompt';
import RatingCard from 'js/social/RatingCard/RatingCard';
import PaperNewsStory from '../CoreX/PaperNewsStory';
import PaperInteractions from '../CoreX/PaperInteractions';
import PaperAbstract from '../CoreX/PaperAbstract';
import { useReader } from '../CoreX/Reader/ReaderProvider';

const PremiumWorkCardsBulkDownloadModal = lazy(
  () =>
    import(
      'js/bulk_download/WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal'
    ),
);

const ResponsiveFollowButton = styled.div`
  display: none;

  @include for-tablet-up {
    display: block;
  }
`;

const Views = styled(BodyBase)`
  background: $ds-off-white;
  color: $ds-gray-dark;
  padding: 0 4px 0;
  margin-top: 4px;
  border-radius: 4px;
  height: 22px;
  white-space: nowrap;
`;

// for display of quote posts with images on mobile
const IndentedImageContainer = styled.div`
  display: block;
  padding-left: 48px;
  @include for-tablet-up {
    display: none;
  }
`;

const ReasonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ShortMenuButtonContainer = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;

const styles = stylesheet`
  .unavailable {
    color: $ds-gray-dark;
  }
`;

const RoundedCorners = styled.div`
  & > * {
    border-radius: var(--border-radius-radius-md);
  }
`;

type NewsProps = News &
  FeedPostsActions &
  FeedCommentsActions & {
    /**
     * Each post only has knowledge of user ids, e.g. post owner,
     * commenter.
     * Information about users exists separately and is referenced by not
     * just a single post. This function lets a post access a particular
     * user's info.
     *
     * @param id ID of a user
     * @return A hash containing user data
     */
    getUserData: (id: number) => FollowableUser;
    index: number;
    nested?: boolean;
    showReason?: boolean;
  };

type QuotedNewsProps = SlimPost &
  FeedPostsActions &
  Pick<NewsProps, 'getUserData'> & {
    parentFeedIndex?: number;
    nested?: boolean;
    showReason?: boolean;
  };

type Props = NewsProps | QuotedNewsProps;

const isQuotedNewsProps = (props: Props): props is QuotedNewsProps => {
  return !Object.prototype.hasOwnProperty.call(props, 'hoisted');
};

type PostEngagementFollowPromptWrappedProps =
  PostEngagementFollowPromptProps & {
    className?: string;
    impressionSource: ImpressionProps['source'];
  };

const PostEngagementFollowPromptWrapped: React.FC<
  PostEngagementFollowPromptWrappedProps
> = ({ className, impressionSource, user, setUserFollowing, followSource }) => {
  return (
    <ImpressionWatcher className={className} source={impressionSource}>
      <PostEngagementFollowPrompt
        user={user}
        setUserFollowing={setUserFollowing}
        followSource={followSource}
      />
    </ImpressionWatcher>
  );
};

const PostEngagementFollowPromptWrappedStyled = styled(
  PostEngagementFollowPromptWrapped,
)`
  &:last-child {
    margin-bottom: 8px;
  }
`;

const openalexPostOwnerReplacement = {
  id: undefined,
  displayName: '',
  following: false,
  url: '',
  firstName: '',
};

const ensureIsFollowableUser = (
  showAuthor: boolean,
  _object: FollowableUser | typeof openalexPostOwnerReplacement,
): _object is FollowableUser => {
  return showAuthor;
};

const NewsComponent: React.FunctionComponent<Props> = (props: Props) => {
  const isQuotedNews = isQuotedNewsProps(props);
  const {
    // These properties are shared between paper_updates and posts
    newsId,
    newsType,
    paperType,
    content,
    tags,
    hashtags,
    work,
    userId,
    getUserData,
    looksDeleted,
    hoisted,
    nested,
    createdAt = Date.now().toString(),
    showReason = true,
  } = props;

  const recommendationMetrics =
    newsType === 'work' || newsType === 'openalex_work'
      ? props.recommendationMetrics
      : undefined;

  const {
    recordViews,
    useReporting,
    feedConfig,
    showInlineReader,
    inActivationOverhaul,
  } = useContext(FeedContext);
  const { page } = feedConfig;

  const [showPostEngagementFollowPrompt, setShowPostEngagementFollowPrompt] =
    useState(false);

  const postOwner = userId ? getUserData(userId) : openalexPostOwnerReplacement;
  const author = useCurrentUserAsAuthor();

  const {
    showHeaderMenu,
    showContent,
    showLikeAction,
    showCommentAction,
    showRepostAction,
    showRecommendationRatings,
    ...elementVisibilities
  } = useElementVisibilities({
    feedConfig,
    isQuotedNews,
    news: props,
    postOwner,
    inActivationOverhaul,
  });
  // Annoying type predicate to make TypeScript happy
  const showAuthor = ensureIsFollowableUser(
    elementVisibilities.showAuthor,
    postOwner,
  );

  const impressionPage = getImpressionPage(page);
  const trackImpressions =
    ['home', 'research_interest'].includes(page) && !isQuotedNews;
  const { followSource, pefpFollowSource } =
    page === 'research_interest'
      ? {
          followSource: 'social_ri_feed',
          pefpFollowSource: 'social_ri_feed_post_engagement_prompt',
        }
      : ({} as { followSource?: string; pefpFollowSource?: string });
  const feedIndex = isQuotedNews ? undefined : props.index;
  const feedReasonType = isQuotedNews ? undefined : props.reason.type;

  const recommendationScore = props.recommendationScore ?? undefined;
  const impressionTrackDimensions = true;

  const IMPRESSION_SECTION = isQuotedNews
    ? `quote-${props.parentFeedIndex}`
    : `feed-${feedIndex}`;
  const IMPRESSION_EXTRA_DATA = isQuotedNews
    ? getImpressionFeedExtraData({
        newsType: 'post',
        newsId,
        feedIndex: props.parentFeedIndex,
        feedReason: 'quote_child',
        recommendationScore,
      })
    : getImpressionFeedExtraData({
        newsType,
        newsId,
        feedIndex,
        feedReason: feedReasonType,
        recommendationScore,
        recommendationMetrics,
        extendedOnsiteRecsTestBucket: feedConfig.extendedOnsiteRecsTestBucket,
        recencyWeightedTestBucket: feedConfig.recencyWeightedTestBucket,
        guessTheResearcherTestBucket: feedConfig.guessTheResearcherTestBucket,
        newsStory: props.newsStory,
      });

  const [impressionElement, impressionEntityId, impressionEntityType] = work
    ? isPaper(work)
      ? ['paper', work.id, ImpressionEntity.Work]
      : ['oa-work', work.id, ImpressionEntity.OpenalexWork]
    : ['post', 0, ImpressionEntity.UnMapped];

  const impressionPaperType = props.newsStory
    ? 'llm-news-story'
    : getImpressionPaperType(paperType);

  const [expandAll, setExpandAll] = useState(page === 'single_post');
  const {
    isShown: showCreateCommentModal,
    show: openCreateCommentModal,
    hide: closeCreateCommentModal,
  } = useShowToggle();
  const {
    isShown: showQuoteNewsModal,
    show: openQuoteNewsModal,
    hide: closeQuoteNewsModal,
  } = useShowToggle();
  const {
    isShown: showCreatePostModal,
    show: openCreatePostModal,
    hide: closeCreatePostModal,
  } = useShowToggle();
  const handleOpenQuoteNewModal = () =>
    paperType === 'bookmark' ? openCreatePostModal() : openQuoteNewsModal();
  const {
    isShown: showEditModal,
    show: openEditModal,
    hide: closeEditModal,
  } = useShowToggle();

  const { actions, permissions, reasonForDismissal } = useNewsInteractions({
    ...props,
    followSource,
  });

  const canCreatePlainRepostFromQuoteNewsModal =
    !isQuotedNews &&
    permissions.canRepost &&
    props.newsType === 'post' &&
    !props.reactions.repost.reacted;

  const {
    modal: confirmationModal,
    showModal: showConfirmationModal,
    ...confirmationModalActions
  } = useConfirmationModal();

  const {
    isShown: showBulkDownloadModal,
    show: openBulkDownloadModal,
    hide: closeBulkDownloadModal,
  } = useShowToggle();

  const {
    followUser,
    openUnfollowUserConfirmationModal,
    openBlockUserConfirmationModal,
    openReportItemConfirmationModal,
    openIgnoreItemConfirmationModal,
    openDeleteItemConfirmationModal,
    openUntagItemConfirmationModal,
  } = useMenuActions(
    postOwner.displayName,
    props.newsType === 'paper_update'
      ? ResourceType.PaperUpdate
      : ResourceType.Post,
    showConfirmationModal,
    confirmationModalActions,
    {
      ...actions,
      reportItem: actions.reportNews,
      ignoreItem: actions.ignoreNews,
      deleteItem: actions.deleteNews,
      removeTag: actions.removeTag,
    },
  );

  const isEligibleForPostEngagementFollowPrompt =
    permissions.canFollow && !postOwner.following;

  const maybeShowPostEngagementFollowPrompt = useCallback(() => {
    if (isEligibleForPostEngagementFollowPrompt) {
      setShowPostEngagementFollowPrompt(true);
    }
  }, [isEligibleForPostEngagementFollowPrompt]);

  const handleLike = useCallback(() => {
    maybeShowPostEngagementFollowPrompt();
    return actions.likeNews();
  }, [actions, maybeShowPostEngagementFollowPrompt]);

  const handleRepost = useCallback(() => {
    maybeShowPostEngagementFollowPrompt();
    return actions.repostNews();
  }, [actions, maybeShowPostEngagementFollowPrompt]);

  const handleCreateQuotedNews = useCallback(
    (payload: ResourcePayload) => {
      maybeShowPostEngagementFollowPrompt();
      return actions.createQuotedNews(payload);
    },
    [actions, maybeShowPostEngagementFollowPrompt],
  );

  const handleDownload = useCallback(() => {
    maybeShowPostEngagementFollowPrompt();
  }, [maybeShowPostEngagementFollowPrompt]);

  const handleBulkDownloadClicked = useCallback(() => {
    openBulkDownloadModal();
    maybeShowPostEngagementFollowPrompt();
  }, [openBulkDownloadModal, maybeShowPostEngagementFollowPrompt]);

  const newsItemImpressionSource = getSocialImpressionSource(
    impressionPage,
    IMPRESSION_SECTION,
    impressionElement,
    impressionPaperType,
  );

  const impressionPropsGen = useMemo(
    () =>
      makeImpressionPropsGenerator(
        impressionPage,
        IMPRESSION_SECTION,
        impressionElement,
        impressionPaperType,
        impressionEntityType,
        impressionEntityId,
        IMPRESSION_EXTRA_DATA,
        impressionTrackDimensions,
      ),
    [
      impressionPage,
      IMPRESSION_SECTION,
      impressionElement,
      impressionPaperType,
      impressionEntityType,
      impressionEntityId,
      IMPRESSION_EXTRA_DATA,
      impressionTrackDimensions,
    ],
  );

  const { showReader } = useReader();
  const showReaderForWork = useCallback(
    () =>
      work &&
      showReader({
        newsType,
        newsId,
        work,
        impressionPropsGen,
      }),
    [showReader, impressionPropsGen, newsType, newsId, work],
  );

  const menuButton = (
    <SocialActionsMenu
      placement="bottom-end"
      menuItems={[
        {
          showItem: permissions.canEdit,
          onClick: openEditModal,
          itemIcon: <Edit size={20} />,
          itemText: 'Edit post',
        },
        {
          showItem: permissions.canIgnore,
          onClick: openIgnoreItemConfirmationModal,
          itemIcon: <HiddenIcon width={20} height={20} />,
          itemText: `I'm not interested in this`,
          impressionProps: {
            entity_type: impressionEntityType,
            entity_id: impressionEntityId,
            source: `${newsItemImpressionSource}_menu_not-interested`,
            extra: IMPRESSION_EXTRA_DATA,
          },
        },
        {
          showItem: permissions.canBulkDownload,
          onClick: handleBulkDownloadClicked,
          itemIcon: <FolderInactive width={20} height={20} />,
          itemText: 'PDF Package',
          impressionProps: {
            entity_type: impressionEntityType,
            entity_id: impressionEntityId,
            source: `${newsItemImpressionSource}_menu_pdf-package`,
            extra: IMPRESSION_EXTRA_DATA,
          },
        },
        {
          showItem: permissions.canUntag,
          onClick: openUntagItemConfirmationModal,
          itemIcon: <UnTagOutlineIcon size={20} />,
          itemText: `Remove tag`,
          itemSubtext: `You won't be tagged in this post anymore.`,
        },
        {
          showItem: showAuthor && permissions.canFollow && postOwner.following,
          onClick: openUnfollowUserConfirmationModal,
          itemIcon: <AccountRemoveOutlineIcon size={20} />,
          itemText: `Unfollow ${postOwner.displayName}`,
        },
        {
          showItem: showAuthor && permissions.canFollow && !postOwner.following,
          onClick: followUser,
          itemIcon: <AddPersonInactive width={20} height={20} />,
          itemText: `Follow ${postOwner.displayName}`,
        },
        {
          showItem: showAuthor && permissions.canBlock,
          onClick: openBlockUserConfirmationModal,
          itemIcon: <BlockIcon width={20} height={20} />,
          itemText: `Block ${postOwner.displayName}`,
        },
        {
          showItem: permissions.canReport,
          onClick: openReportItemConfirmationModal,
          itemIcon: <FlagInactive width={20} height={20} />,
          itemText: 'Report post',
          impressionProps: {
            entity_type: impressionEntityType,
            entity_id: impressionEntityId,
            source: `${newsItemImpressionSource}_menu_report`,
            extra: IMPRESSION_EXTRA_DATA,
          },
        },
        {
          showItem: permissions.canDelete,
          onClick: openDeleteItemConfirmationModal,
          itemIcon: <DeleteInactive />,
          itemText: 'Delete',
        },
      ]}
    />
  );

  const {
    isShown: showComments,
    toggle: toggleCommentSection,
    show: showCommentSection,
  } = useShowToggle(
    !isQuotedNews &&
      props.comments.data.some((comment) => comment.expandByDefault),
  );

  const recordPostView = () => {
    if (!isQuotedNews && currentUser != null) {
      if (useReporting && userId && createdAt) {
        legacyRecordNewsfeedView(currentUser.id, newsType, {
          id: newsId,
          userId,
          createdAt,
          index: props.index,
        });
        fsEvent('UserViewedSocial', {
          newsId,
          newsType,
          numCommentsOnView: props.comments.count,
          numReactionsOnView: props.reactions.like.count,
        });
      }

      if (recordViews) {
        const key = {
          post: 'postId',
          paper_update: 'paperUpdateId',
          paper_highlight: 'workId',
          work: 'workId',
          openalex_work: 'openalexWorkId',
          paper_set: 'paperSetId',
        }[newsType];

        if (key) {
          return createNewsView({
            userId: currentUser.id,
            [key]: newsId,
            numCommentsOnView: props.comments.count,
            numReactionsOnView: props.reactions.like.count,
          }).then(() => {});
        } else {
          return Promise.resolve();
        }
      }
    }
    return Promise.resolve();
  };

  const newsContentBody: React.ReactNode = (
    <>
      {showContent &&
        content !== null &&
        (content.length > 0 || tags.length > 0) && (
          <PostBody
            content={content}
            tags={tags}
            hashtags={hashtags}
            // Allow all hashtags for rate-limited posts
            allowAllHashtags={newsId === 0}
            isQuotedNews={isQuotedNews}
            isSinglePostPage={page === 'single_post'}
            isTextOnly={
              newsType === 'post' &&
              !isQuotedNews &&
              !props.quotable &&
              !props.image &&
              !props.work
            }
            trackImpressions={trackImpressions}
            impressionExtraData={IMPRESSION_EXTRA_DATA}
            impressionSourceRoot={getSocialImpressionSource(
              impressionPage,
              IMPRESSION_SECTION,
            )}
          />
        )}

      {newsType === 'post' &&
        props.image &&
        !looksDeleted &&
        (isQuotedNews ? (
          page === 'single_post' ? (
            <div style={{ paddingLeft: '48px' }}>
              <PostImage
                title={postOwner.displayName}
                caption={content}
                image={props.image}
              />
            </div>
          ) : (
            <IndentedImageContainer>
              <PostImage
                title={postOwner.displayName}
                caption={content}
                image={props.image}
              />
            </IndentedImageContainer>
          )
        ) : (
          <div>
            <PostImage
              title={postOwner.displayName}
              caption={content}
              image={props.image}
            />
          </div>
        ))}

      {work && !looksDeleted && (
        <ImpressionWatcher
          active={trackImpressions}
          {...impressionPropsGen()()()}
          extra={{
            ...IMPRESSION_EXTRA_DATA,
            readable: showInlineReader && work?.attachment?.readable,
          }}
        >
          {props.newsStory ? (
            <RoundedCorners>
              <PaperNewsStory
                newsStory={props.newsStory}
                work={work}
                canUseReader={
                  !!showInlineReader && !!work?.attachment?.readable
                }
                showReader={showReaderForWork}
                impressionPropsGen={impressionPropsGen()()}
                paperInteractions={
                  <PaperInteractions
                    work={work}
                    showBookmark={
                      !work.authors
                        .map((a) => a.userId)
                        .includes(currentUser?.id ?? 0) &&
                      !feedConfig.openalexRecsTestBucket
                    }
                    canUseReader={
                      !!showInlineReader && !!work?.attachment?.readable
                    }
                    showReader={showReaderForWork}
                    isSnippet={!!props.newsStory}
                    isBookmarked={permissions.isBookmarked ?? false}
                    onBookmark={actions.bookmarkWork}
                    onUnbookmark={actions.unbookmarkWork}
                    impressionPropsGen={impressionPropsGen()()}
                  />
                }
              />
            </RoundedCorners>
          ) : !isQuotedNews && props.reason.type === 'recommended' ? (
            <PaperAbstract
              work={work}
              canUseReader={!!showInlineReader && !!work?.attachment?.readable}
              showReader={showReaderForWork}
              impressionPropsGen={impressionPropsGen()()}
              paperInteractions={
                <PaperInteractions
                  work={work}
                  showBookmark={
                    !work.authors
                      .map((a) => a.userId)
                      .includes(currentUser?.id ?? 0) &&
                    !feedConfig.openalexRecsTestBucket
                  }
                  canUseReader={
                    !!showInlineReader && !!work?.attachment?.readable
                  }
                  showReader={showReaderForWork}
                  isSnippet={false}
                  isBookmarked={permissions.isBookmarked ?? false}
                  onBookmark={actions.bookmarkWork}
                  onUnbookmark={actions.unbookmarkWork}
                  impressionPropsGen={impressionPropsGen()()}
                />
              }
            />
          ) : (
            <PaperContainer
              style={{ paddingLeft: isQuotedNews ? '48px' : '0' }}
              work={work}
              showBookmark={
                !work.authors
                  .map((a) => a.userId)
                  .includes(currentUser?.id ?? 0) &&
                !feedConfig.openalexRecsTestBucket
              }
              showDownload
              showInlineReader={showInlineReader}
              isBookmarked={permissions.isBookmarked}
              downloadUrl={work?.attachment?.pdfUrl ?? work?.url}
              onBookmark={actions.bookmarkWork}
              onUnbookmark={actions.unbookmarkWork}
              onDownload={handleDownload}
              impressionProps={impressionPropsGen()()()}
            />
          )}
        </ImpressionWatcher>
      )}
    </>
  );

  const newsKey = `${newsType}:${newsId}:${content}:${tags}`;
  let newsContent: React.ReactNode;
  if (isQuotedNews && looksDeleted) {
    newsContent = (
      <div className={styles.unavailable}>
        This content isn't available right now.
      </div>
    );
  } else if (isQuotedNews) {
    newsContent = newsContentBody;
  } else if (looksDeleted) {
    newsContent = (
      <PostBody
        className={styles.unavailable}
        content={"This content isn't available right now."}
      />
    );
  } else if (newsType === 'post') {
    newsContent = (
      <ViewsWatcher
        key={newsKey}
        type={newsType}
        id={newsId}
        sendView={recordPostView}
        threshold={0.8}
      >
        {newsContentBody}

        {props.quotable && (
          <NewsComponent
            {...props.quotable}
            getUserData={props.getUserData}
            setUserFollowing={props.setUserFollowing}
            removeUserContent={props.removeUserContent}
            removeFeedNews={(id: string | number, type: NewsType) => {
              props.removeFeedNews(id, type);
              if (
                id === props.quotable?.newsId &&
                type === props.quotable?.newsType
              ) {
                // If the user reports the quoted post, then this post should also be removed.
                props.removeFeedNews(newsId, newsType);
              }
            }}
            // No-op feed actions because a quoted news should not be doing these
            addFeedNews={() => {}}
            addFeedComment={() => {}}
            updateFeedNews={() => {}}
            likeFeedNews={() => {}}
            unlikeFeedNews={() => {}}
            repostFeedNews={() => {}}
            unrepostFeedNews={() => {}}
            updateWorkBookmarkStatus={() => {}}
            parentFeedIndex={props.index}
            nested
          />
        )}
      </ViewsWatcher>
    );
  } else {
    newsContent = (
      <ViewsWatcher
        key={newsKey}
        type={newsType}
        id={newsId}
        sendView={recordPostView}
        threshold={0.8}
      >
        {newsContentBody}
      </ViewsWatcher>
    );
  }

  const handleUndoDismissal = useCallback(async () => {
    if (reasonForDismissal === DismissalReason.Ignored)
      return actions.unIgnoreNews();
    else if (reasonForDismissal === DismissalReason.Reported)
      return actions.unReportNews();
    return () => {};
  }, [reasonForDismissal, actions]);

  return reasonForDismissal ? (
    <RemovedItemNotice undoRemoval={handleUndoDismissal} />
  ) : (
    <Base
      reason={
        !isQuotedNews &&
        (showReason || !showAuthor) && (
          <ReasonContainer>
            <div>
              {showReason && (
                <Reason
                  type={
                    props.reason.type ||
                    // SOC-2117: if there's no author info, and no reason, the post looks empty without a reason.
                    //           Fallback to 'recommended'
                    (showAuthor ? null : 'recommended')
                  }
                  users={(props.reason.data?.sourceUserIds || []).map(
                    getUserData,
                  )}
                  totalUsers={props.reason.data?.count ?? 0}
                />
              )}
            </div>
            {!showAuthor && showHeaderMenu && (
              <ShortMenuButtonContainer>{menuButton}</ShortMenuButtonContainer>
            )}
          </ReasonContainer>
        )
      }
      header={
        showAuthor && (!isQuotedNews || !looksDeleted) && showHeaderMenu ? (
          <Header
            user={postOwner}
            userUrl={postOwner.url}
            createdAt={createdAt}
            newsId={newsId}
            newsType={newsType}
            paperType={paperType}
            cta={
              <>
                <ResponsiveFollowButton>
                  <FollowActionButton
                    variant="text"
                    userId={postOwner.id}
                    follows={postOwner.following}
                    onFollow={followUser}
                    onUnfollow={openUnfollowUserConfirmationModal}
                  />
                </ResponsiveFollowButton>

                {menuButton}
              </>
            }
            isInQuotedNews={isQuotedNews}
            feedIndex={feedIndex}
            feedReason={feedReasonType}
            recommendationScore={recommendationScore}
          />
        ) : null
      }
      content={newsContent}
      cta={
        !isQuotedNews &&
        !looksDeleted && (
          <Cta
            like={{
              liked: props.reactions.like.reacted,
              likeNews: handleLike,
              unlikeNews: actions.unlikeNews,
            }}
            repost={
              permissions.canRepost
                ? {
                    reposted: props.reactions.repost.reacted,
                    repostNews: handleRepost,
                    unrepostNews: actions.unrepostNews,
                  }
                : undefined
            }
            onCommentClick={openCreateCommentModal}
            onQuoteClick={handleOpenQuoteNewModal}
            hideLikeAction={!showLikeAction}
            hideCommentAction={!showCommentAction}
            hideRepostAction={!showRepostAction}
            impressionProps={{
              source: newsItemImpressionSource,
              entity_type: impressionEntityType,
              entity_id: impressionEntityId,
              extra: IMPRESSION_EXTRA_DATA,
            }}
          >
            {!isQuotedNews &&
              !looksDeleted &&
              (props.reactions.like.count > 0 || props.comments.count > 0) && (
                <Stats
                  newsId={newsId}
                  newsType={newsType}
                  numLikes={props.reactions.like.count}
                  numComments={props.comments.count}
                  toggleCommentSection={() => {
                    toggleCommentSection();
                    setExpandAll(true);
                  }}
                />
              )}
            {props.views !== undefined && (
              <Views size="sm">
                {new Intl.NumberFormat().format(props.views)}{' '}
                {pluralize('viewer', props.views)}
              </Views>
            )}
          </Cta>
        )
      }
      isQuotedNews={isQuotedNews}
      hoisted={hoisted}
      image={newsType === 'post' ? props.image : undefined}
      isSinglePostPage={page === 'single_post'}
      newsType={newsType}
      newsId={newsId}
      looksDeleted={looksDeleted}
      nested={nested}
    >
      {showRecommendationRatings && (
        <RatingCard
          newsType={newsType}
          newsId={newsId}
          reason={isQuotedNews ? undefined : props.reason.type}
        />
      )}
      {showConfirmationModal && confirmationModal}
      {!isQuotedNews && !looksDeleted && showCreateCommentModal && (
        <CreateSocialItem
          id={`${newsType}:${newsId}:comment`}
          type={ResourceType.Comment}
          author={author}
          heading={`Comment on ${postOwner.firstName}'s post`}
          placeholder={`Add a comment...`}
          cta="Comment"
          onClose={closeCreateCommentModal}
          onSubmit={(payload) => {
            actions.createComment(payload);
            showCommentSection();
          }}
        >
          <Base
            header={
              showAuthor && (
                <Header
                  user={postOwner}
                  newsId={newsId}
                  newsType={newsType}
                  paperType={paperType}
                  createdAt={createdAt}
                />
              )
            }
            content={newsContentBody}
            nested
          />
        </CreateSocialItem>
      )}
      {!isQuotedNews && !looksDeleted && showQuoteNewsModal && (
        <CreateSocialItem
          id={`${newsType}:${newsId}:quote`}
          type={ResourceType.Post}
          author={author}
          heading={`Repost ${postOwner.firstName}'s post`}
          placeholder={`Repost with a thought...`}
          cta="Repost"
          disableImages
          disablePapers
          onClose={closeQuoteNewsModal}
          onSubmit={handleCreateQuotedNews}
          inputRequired={!canCreatePlainRepostFromQuoteNewsModal}
        >
          <Base
            header={
              showAuthor && (
                <Header
                  user={postOwner}
                  newsId={newsId}
                  newsType={newsType}
                  paperType={paperType}
                  createdAt={createdAt}
                />
              )
            }
            content={newsContentBody}
            nested
          />
        </CreateSocialItem>
      )}
      {!isQuotedNews &&
        !looksDeleted &&
        showCreatePostModal &&
        isPaper(work) && (
          <PostPrompt
            open={showCreatePostModal}
            onClose={closeCreatePostModal}
            modalOnly={true}
            fullName={currentUser?.display_name || ''}
            photoUrl={''}
            profileUrl={currentUser?.url || ''}
            initialPaper={work}
          />
        )}
      {showEditModal && (
        <CreateSocialItem
          id={`${newsType}:${newsId}:edit`}
          type={
            newsType === 'paper_update'
              ? ResourceType.PaperUpdate
              : ResourceType.Post
          }
          author={author}
          heading={`Edit post`}
          placeholder="Share a thought with other scholars"
          cta="Save Edits"
          disableImages
          disablePapers
          onClose={closeEditModal}
          onSubmit={(payload) => {
            actions.editNews(payload);
          }}
          initialContent={content === null ? undefined : content}
          initialTags={tags}
          initialImage={newsType === 'post' ? props.image : undefined}
          initialPaper={props.work as Paper}
          attachmentRemovable={false}
        />
      )}
      {newsType === 'paper_update' &&
        showBulkDownloadModal &&
        !looksDeleted && (
          <Suspense fallback={null}>
            <PremiumWorkCardsBulkDownloadModal
              onClose={closeBulkDownloadModal}
              sourceWork={{
                id: props.work.id,
                title: props.work.title,
                slug: props.work.slug,
                hasDownloadableAttachment: true,
              }}
            />
          </Suspense>
        )}
      {
        // SOC-1990: it doesn't really make sense to show the PEFP if the user information isn't shown in the first place
        showAuthor && showPostEngagementFollowPrompt && (
          <PostEngagementFollowPromptWrappedStyled
            impressionSource={getSocialImpressionSource(
              impressionPage,
              IMPRESSION_SECTION,
              'pefp',
            )}
            user={postOwner}
            setUserFollowing={props.setUserFollowing}
            followSource={pefpFollowSource}
          />
        )
      }
      {!isQuotedNews && !looksDeleted && (
        <PostCommentsBase
          newsType={newsType}
          commentableId={newsId}
          comments={props.comments}
          show={showComments}
          expandAll={expandAll}
          getUserData={getUserData}
          setUserFollowing={props.setUserFollowing}
          removeUserContent={props.removeUserContent}
          addFeedComment={props.addFeedComment}
          updateFeedComment={props.updateFeedComment}
          removeFeedComment={props.removeFeedComment}
          likeFeedComment={props.likeFeedComment}
          unlikeFeedComment={props.unlikeFeedComment}
        />
      )}
    </Base>
  );
};

export default NewsComponent;
